/**
 * LotsProductionPage - Gestion des Lots de Production
 * Architecture Intégrée : Parcelle -> Intervention (récolte) -> Lot -> Stock -> Client -> Facture
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Search,
  MapPin,
  Calendar,
  Scale,
  Leaf,
  Award,
  TrendingUp,
  FileText,
  Loader2,
  Eye,
  Edit,
  Trash2,
  ArrowRight,
  Link,
  Users,
  Euro,
  Warehouse,
  ChevronRight,
  QrCode,
  History,
  RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';

const LotsProductionPage = () => {
  const [loading, setLoading] = useState(true);
  const [lots, setLots] = useState([]);
  const [stats, setStats] = useState({});
  const [parcelles, setParcelles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProduit, setFilterProduit] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [selectedLot, setSelectedLot] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Formulaire nouveau lot
  const [newLot, setNewLot] = useState({
    parcelle_id: '',
    produit: '',
    variete: '',
    qualite: 'A',
    quantite: '',
    unite: 'kg',
    date_recolte: new Date().toISOString().split('T')[0],
    certification: '',
    emplacement_stockage: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lotsRes, statsRes, parcellesRes] = await Promise.all([
        api.get('/lots').catch(() => ({ data: { lots: [] } })),
        api.get('/lots/stats').catch(() => ({ data: {} })),
        api.get('/parcellaire/parcelles').catch(() => ({ data: { parcelles: [] } })),
      ]);
      
      setLots(lotsRes.data?.lots || lotsRes.data || []);
      setStats(statsRes.data || {});
      setParcelles(parcellesRes.data?.parcelles || []);
    } catch (error) {
      console.error('Erreur chargement lots:', error);
      toast.error('Erreur lors du chargement des lots');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLot = async () => {
    if (!newLot.parcelle_id || !newLot.produit || !newLot.quantite) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      const response = await api.post('/lots', {
        ...newLot,
        quantite: parseFloat(newLot.quantite),
      });
      
      if (response.data?.success) {
        toast.success(`Lot ${response.data.lot?.numero_lot} créé avec succès`);
        setShowCreateDialog(false);
        setNewLot({
          parcelle_id: '',
          produit: '',
          variete: '',
          qualite: 'A',
          quantite: '',
          unite: 'kg',
          date_recolte: new Date().toISOString().split('T')[0],
          certification: '',
          emplacement_stockage: '',
          notes: '',
        });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur création lot');
    }
  };

  const openLotDetail = async (lot) => {
    setSelectedLot(lot);
    setShowDetailDialog(true);
    
    // Charger les détails complets
    try {
      const response = await api.get(`/lots/${lot.id}`);
      setSelectedLot(response.data);
    } catch (error) {
      console.error('Erreur chargement détail lot:', error);
    }
  };

  // Filtrage des lots
  const filteredLots = lots.filter(lot => {
    const matchSearch = !searchQuery || 
      lot.numero_lot?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lot.produit?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lot.parcelle_nom?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchProduit = filterProduit === 'all' || lot.produit === filterProduit;
    const matchStatut = filterStatut === 'all' || lot.statut === filterStatut;
    
    return matchSearch && matchProduit && matchStatut;
  });

  // Produits uniques pour le filtre
  const produitsUniques = [...new Set(lots.map(l => l.produit).filter(Boolean))];

  const getStatutBadge = (statut) => {
    const styles = {
      disponible: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      reserve: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      vendu: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      epuise: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return styles[statut] || styles.disponible;
  };

  const getCertificationBadge = (cert) => {
    const styles = {
      bio: 'bg-green-500 text-white',
      hve: 'bg-emerald-500 text-white',
      ffl: 'bg-purple-500 text-white',
      demeter: 'bg-orange-500 text-white',
    };
    return styles[cert?.toLowerCase()] || 'bg-gray-500 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="lots-production-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Lots de Production</h1>
          <p className="text-muted-foreground">Traçabilité complète : Parcelle - Récolte - Stock - Vente</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-lot-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Lot
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="w-4 h-4" />
              <span className="text-sm">Total Lots</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_lots || lots.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Warehouse className="w-4 h-4" />
              <span className="text-sm">En Stock</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {stats.lots_disponibles || lots.filter(l => l.quantite_disponible > 0).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Scale className="w-4 h-4" />
              <span className="text-sm">Quantité Totale</span>
            </div>
            <p className="text-2xl font-bold">
              {(stats.quantite_totale || lots.reduce((acc, l) => acc + (l.quantite || 0), 0)).toLocaleString()} kg
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Euro className="w-4 h-4" />
              <span className="text-sm">Valeur Stock</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {(stats.valeur_stock || 0).toLocaleString()} EUR
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par numéro, produit, parcelle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-lots"
          />
        </div>
        <Select value={filterProduit} onValueChange={setFilterProduit}>
          <SelectTrigger className="w-48" data-testid="filter-produit">
            <SelectValue placeholder="Produit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les produits</SelectItem>
            {produitsUniques.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-48" data-testid="filter-statut">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="disponible">Disponible</SelectItem>
            <SelectItem value="reserve">Réservé</SelectItem>
            <SelectItem value="vendu">Vendu</SelectItem>
            <SelectItem value="epuise">Épuisé</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Liste des lots */}
      <div className="grid gap-4">
        {filteredLots.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Aucun lot trouvé</p>
              <p className="text-muted-foreground">Créez votre premier lot de production</p>
            </CardContent>
          </Card>
        ) : (
          filteredLots.map(lot => (
            <Card 
              key={lot.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openLotDetail(lot)}
              data-testid={`lot-card-${lot.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{lot.numero_lot}</h3>
                          <Badge className={getStatutBadge(lot.statut)}>
                            {lot.statut || 'disponible'}
                          </Badge>
                          {lot.certification && (
                            <Badge className={getCertificationBadge(lot.certification)}>
                              {lot.certification.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {lot.produit} {lot.variete && `- ${lot.variete}`}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>{lot.parcelle_nom || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{lot.date_recolte ? new Date(lot.date_recolte).toLocaleDateString('fr-FR') : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Scale className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">
                          {lot.quantite_disponible?.toLocaleString() || lot.quantite?.toLocaleString()} {lot.unite}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Warehouse className="w-4 h-4 text-muted-foreground" />
                        <span>{lot.emplacement_stockage || 'Non défini'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog Création Lot */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau Lot de Production</DialogTitle>
            <DialogDescription>
              Enregistrez une nouvelle récolte avec traçabilité complète
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parcelle *</Label>
                <Select 
                  value={newLot.parcelle_id} 
                  onValueChange={(v) => setNewLot({...newLot, parcelle_id: v})}
                >
                  <SelectTrigger data-testid="select-parcelle">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {parcelles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nom} ({p.surface} ha)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date récolte *</Label>
                <Input
                  type="date"
                  value={newLot.date_recolte}
                  onChange={(e) => setNewLot({...newLot, date_recolte: e.target.value})}
                  data-testid="input-date-recolte"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Produit *</Label>
                <Input
                  placeholder="Ex: Lavande, Blé, Raisin..."
                  value={newLot.produit}
                  onChange={(e) => setNewLot({...newLot, produit: e.target.value})}
                  data-testid="input-produit"
                />
              </div>
              <div className="space-y-2">
                <Label>Variété</Label>
                <Input
                  placeholder="Ex: Fine, Grosso..."
                  value={newLot.variete}
                  onChange={(e) => setNewLot({...newLot, variete: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantité *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newLot.quantite}
                  onChange={(e) => setNewLot({...newLot, quantite: e.target.value})}
                  data-testid="input-quantite"
                />
              </div>
              <div className="space-y-2">
                <Label>Unité</Label>
                <Select 
                  value={newLot.unite} 
                  onValueChange={(v) => setNewLot({...newLot, unite: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="T">Tonnes</SelectItem>
                    <SelectItem value="hl">Hectolitres</SelectItem>
                    <SelectItem value="L">Litres</SelectItem>
                    <SelectItem value="bottes">Bottes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Qualité</Label>
                <Select 
                  value={newLot.qualite} 
                  onValueChange={(v) => setNewLot({...newLot, qualite: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Qualité A</SelectItem>
                    <SelectItem value="B">Qualité B</SelectItem>
                    <SelectItem value="C">Qualité C</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Certification</Label>
                <Select 
                  value={newLot.certification} 
                  onValueChange={(v) => setNewLot({...newLot, certification: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucune" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Aucune</SelectItem>
                    <SelectItem value="bio">Bio</SelectItem>
                    <SelectItem value="hve">HVE</SelectItem>
                    <SelectItem value="ffl">Fair For Life</SelectItem>
                    <SelectItem value="demeter">Demeter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Emplacement stockage</Label>
                <Input
                  placeholder="Ex: Hangar A, Silo 3..."
                  value={newLot.emplacement_stockage}
                  onChange={(e) => setNewLot({...newLot, emplacement_stockage: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Observations, conditions de récolte..."
                value={newLot.notes}
                onChange={(e) => setNewLot({...newLot, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateLot} data-testid="confirm-create-lot">
              Créer le lot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Détail Lot */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              {selectedLot?.numero_lot}
            </DialogTitle>
            <DialogDescription>
              Traçabilité et historique du lot
            </DialogDescription>
          </DialogHeader>

          {selectedLot && (
            <div className="space-y-6 py-4">
              {/* Infos principales */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground">Produit</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold">{selectedLot.produit}</p>
                    {selectedLot.variete && <p className="text-sm text-muted-foreground">{selectedLot.variete}</p>}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground">Quantité disponible</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-green-600">
                      {selectedLot.quantite_disponible?.toLocaleString() || selectedLot.quantite?.toLocaleString()} {selectedLot.unite}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      / {selectedLot.quantite?.toLocaleString()} {selectedLot.unite} initial
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Traçabilité */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Traçabilité
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedLot.parcelle_nom}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Récolte {new Date(selectedLot.date_recolte).toLocaleDateString('fr-FR')}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {selectedLot.numero_lot}
                    </Badge>
                    {selectedLot.clients_ids?.length > 0 && (
                      <>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {selectedLot.clients_ids.length} client(s)
                        </Badge>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Historique des ventes */}
              {selectedLot.ventes && selectedLot.ventes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Ventes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedLot.ventes.map((vente, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div>
                            <p className="font-medium">{vente.client_nom}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(vente.date_vente).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{vente.quantite_vendue} {selectedLot.unite}</p>
                            <p className="text-sm text-muted-foreground">
                              {vente.prix_unitaire} EUR/{selectedLot.unite}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mouvements */}
              {selectedLot.mouvements && selectedLot.mouvements.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Mouvements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedLot.mouvements.map((mouv, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 border-b last:border-0">
                          <div>
                            <p className="font-medium capitalize">{mouv.type_mouvement}</p>
                            <p className="text-sm text-muted-foreground">{mouv.motif}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-medium ${mouv.type_mouvement === 'entree' ? 'text-green-600' : 'text-red-600'}`}>
                              {mouv.type_mouvement === 'entree' ? '+' : '-'}{mouv.quantite} {selectedLot.unite}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(mouv.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LotsProductionPage;
